import React from 'react';
import { List } from 'antd';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { DeleteIcon, PencilIcon } from '@/assets/icons/icons';
import styles from './ContactList.module.scss';

interface Contact {
  name: string;
  title: string;
  email: string;
  phone_number: string;
  is_primary_contact: boolean;
  /** Set on saved contacts only. */
  created_at?: string;
}

/**
 * The contact a workflow reaches as this location's secondary contact: the oldest saved
 * contact that isn't the primary, or the second oldest when none is marked primary
 * (logiblocks/graph/contact_utils.py, which also skips a contact that can't be reached
 * on the step's channel).
 */
export function findSecondaryContact<T extends Contact>(
  contacts: readonly T[]
): T | undefined {
  const hasPrimary = contacts.some((contact) => contact.is_primary_contact);
  const saved = contacts
    .filter((contact) => !contact.is_primary_contact && contact.created_at)
    .sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''));
  return hasPrimary ? saved[0] : saved[1];
}

interface ContactListProps {
  handleEdit: (contact: Contact, index: number) => void;
  handleDeleteClick: (contact: Contact, index: number) => void;
  data: Contact[] | null | undefined;
}

/**
 * ContactList Component
 *
 * Displays a list of contacts with edit and delete functionality.
 * Formats phone numbers and highlights primary contacts.
 *
 * @param handleEdit - Callback function for editing a contact
 * @param handleDeleteClick - Callback function for deleting a contact
 * @param data - Array of contact objects to display
 *
 * @returns Contact list or null if no data
 */
const ContactList: React.FC<ContactListProps> = ({
  handleEdit,
  handleDeleteClick,
  data,
}) => {
  if (!data || data.length === 0) {
    return null;
  }

  const sortedData = [...data].sort((a, b) => {
    if (!!a.is_primary_contact === !!b.is_primary_contact) return 0;
    return a.is_primary_contact ? -1 : 1;
  });
  const secondaryContact = findSecondaryContact(data);

  const formatPhoneNumber = (number: string): string => {
    if (!number) return '';
    const cleaned = number.replace(/[^+\d]/g, '');

    try {
      const parsed = parsePhoneNumberFromString(cleaned);
      if (!parsed) return number;
      if (cleaned.startsWith('+1')) {
        const national = String(parsed.nationalNumber);
        if (national.length === 10) {
          const areaCode = national.slice(0, 3);
          const centralOffice = national.slice(3, 6);
          const lineNumber = national.slice(6);
          return `+1(${areaCode})-${centralOffice}-${lineNumber}`;
        }
      }

      return parsed.formatInternational();
    } catch {
      return number;
    }
  };

  return (
    <List
      dataSource={sortedData}
      renderItem={(contact: Contact, index: number) => (
        <List.Item
          key={contact.email || `${contact.name}-${index}`}
          actions={[
            <span
              key='edit'
              role='button'
              aria-label='Edit'
              className={`cursor-pointer ${styles.action}`}
              onClick={() => handleEdit(contact, index)}
            >
              <PencilIcon color='var(--action-icon-color)' />
            </span>,
            <span
              key='delete'
              role='button'
              aria-label='Delete'
              className={`cursor-pointer ${styles.action}`}
              onClick={() => handleDeleteClick(contact, index)}
            >
              <DeleteIcon color='var(--action-icon-color)' />
            </span>,
          ]}
          className={`custom-contact-list ${styles.item}`}
        >
          <List.Item.Meta
            title={
              <span className={styles.name}>
                {contact.name}, {contact.title}{' '}
                {contact.is_primary_contact ? '(primary)' : ''}
                {contact === secondaryContact ? '(secondary)' : ''}
              </span>
            }
            description={
              <>
                <a href={`mailto:${contact.email}`} className={styles.email}>
                  {contact.email}
                </a>
                {contact?.email && contact?.phone_number ? ', ' : ''}
                <span className={styles.phone}>
                  {formatPhoneNumber(contact.phone_number)}
                </span>
              </>
            }
          />
        </List.Item>
      )}
    />
  );
};

export default ContactList;
